import React, { useState, useEffect } from 'react'
import { Modal, Select, Input, Button, Form, message, Space, Divider } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import useSWR from 'swr'
import API from '../../../utils/api'
import ExchangeName from '../ExchangeName'
import { useToken } from '../../hooks'

const ModalContent = styled.div`
  .ant-form-item {
    margin-bottom: 16px;
  }
`

const PairInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 16px;
  
  .pair-code {
    font-weight: bold;
    color: #1890ff;
  }
  
  .group-name {
    color: #666;
    font-size: 12px;
  }
`

interface AddPersonalContractModalProps {
  visible: boolean
  onCancel: () => void
  onSuccess: () => void
  userid: string
}

const AddPersonalContractModal: React.FC<AddPersonalContractModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  userid
}) => {
  const { data: token } = useToken()
  const [form] = Form.useForm()
  const [selectedPair, setSelectedPair] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  // 기존 시스템과 동일한 방식으로 전체 종목+개인별계약 정보 가져오기
  const { data: allPairsWithPersonal, error, isLoading } = useSWR(
    token && userid ? `/admin/exchangepairs?ov=1&userid=${userid}` : null,
    async (url) => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        })
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const result = await response.json()
        console.log('전체 종목+개인별계약 목록:', result) // 디버깅용
        return Array.isArray(result) ? result : (result.result || result.data || [])
      } catch (error) {
        console.error('종목 목록 로드 에러:', error)
        throw error
      }
    }
  )

  // 설정 가능한 종목 목록 (person 필드가 없는 항목만)
  const availablePairs = React.useMemo(() => {
    if (!allPairsWithPersonal || !Array.isArray(allPairsWithPersonal)) return []
    
    // person 필드가 없는(개인설정되지 않은) 항목만 반환
    return allPairsWithPersonal.filter(pair => !pair.person)
  }, [allPairsWithPersonal])

  const handleSubmit = async (values: any) => {
    if (!selectedPair) {
      message.error('종목을 선택해주세요.')
      return
    }

    setLoading(true)
    try {
      await API.send('PUT', '/admin/personpairs', token || '', {
        ov: selectedPair.pairs,
        userid: userid,
        margin: values.margin || selectedPair.minimum_margin,
        losscut: values.losscut || selectedPair.losscut,
        ovnmargin: values.ovnmargin || selectedPair.ovn_margin,
        max_amount: values.max_amount || selectedPair.max_amount,
        fee: values.fee || selectedPair.fee,
        feetype: values.feetype !== undefined ? values.feetype : selectedPair.feetype,
        slip: values.slip || selectedPair.slip || 0,
      })
      
      message.success('개인별계약이 설정되었습니다.')
      form.resetFields()
      setSelectedPair(null)
      onSuccess()
      onCancel()
    } catch (error) {
      message.error('개인별계약 설정에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handlePairSelect = (pairId: string) => {
    const pair = availablePairs.find(p => p.pairs === pairId)
    setSelectedPair(pair)
    
    // 폼에 기본값 설정
    if (pair) {
      form.setFieldsValue({
        margin: pair.minimum_margin,
        losscut: pair.losscut,
        ovnmargin: pair.ovn_margin,
        max_amount: pair.max_amount,
        fee: pair.fee,
        feetype: pair.feetype,
        slip: pair.slip || 0,
      })
    }
  }

  const resetForm = () => {
    form.resetFields()
    setSelectedPair(null)
  }

  useEffect(() => {
    if (!visible) {
      resetForm()
    }
  }, [visible])

  return (
    <Modal
      title="개인별계약 추가"
      open={visible}
      onCancel={onCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          취소
        </Button>,
        <Button key="reset" onClick={resetForm}>
          초기화
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
          disabled={!selectedPair}
        >
          설정
        </Button>
      ]}
    >
      <ModalContent>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="종목 선택"
            name="pairs"
            rules={[{ required: true, message: '종목을 선택해주세요.' }]}
          >
            <Select
              placeholder={
                isLoading 
                  ? "종목 목록을 불러오는 중..." 
                  : availablePairs.length === 0
                  ? "설정 가능한 종목이 없습니다."
                  : "설정할 종목을 선택해주세요"
              }
              showSearch
              loading={isLoading}
              disabled={isLoading || availablePairs.length === 0}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={handlePairSelect}
              options={availablePairs.map(pair => ({
                value: pair.pairs,
                label: `${pair.pairs} (${pair.groupname})`,
              }))}
              notFoundContent={
                error ? "종목 로드 중 오류가 발생했습니다." : "종목이 없습니다."
              }
            />
          </Form.Item>

          {error && (
            <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '8px' }}>
              ⚠️ 종목 목록을 불러오는데 실패했습니다. 새로고침 후 다시 시도해주세요.
            </div>
          )}

          {!isLoading && availablePairs.length === 0 && !error && (
            <div style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
              💡 모든 종목이 이미 설정되어 있습니다.
            </div>
          )}

          {selectedPair && (
            <>
              <PairInfo>
                <span className="pair-code">{selectedPair.pairs}</span>
                <ExchangeName exchange_id={selectedPair.exchange_id} />
                <span className="group-name">{selectedPair.groupname}</span>
                <span style={{ fontSize: '12px', color: '#999' }}>
                  코드: {selectedPair.code}
                </span>
              </PairInfo>

              <Divider orientation="left">💰 증거금 설정</Divider>
              
              <Space.Compact style={{ display: 'flex', gap: '8px' }}>
                <Form.Item
                  label="증거금"
                  name="margin"
                  style={{ flex: 1 }}
                >
                  <Input
                    type="number"
                    placeholder="증거금"
                    addonAfter="원"
                  />
                </Form.Item>

                <Form.Item
                  label="로스컷"
                  name="losscut"
                  style={{ flex: 1 }}
                >
                  <Input
                    type="number"
                    placeholder="로스컷"
                    addonAfter="원"
                  />
                </Form.Item>
              </Space.Compact>

              <Space.Compact style={{ display: 'flex', gap: '8px' }}>
                <Form.Item
                  label="오버나잇"
                  name="ovnmargin"
                  style={{ flex: 1 }}
                >
                  <Input
                    type="number"
                    placeholder="오버나잇"
                    addonAfter="원"
                  />
                </Form.Item>

                <Form.Item
                  label="최대계약수"
                  name="max_amount"
                  style={{ flex: 1 }}
                >
                  <Input
                    type="number"
                    placeholder="최대계약수"
                  />
                </Form.Item>
              </Space.Compact>

              <Divider orientation="left">💳 수수료 설정</Divider>

              <Space.Compact style={{ display: 'flex', gap: '8px' }}>
                <Form.Item
                  label="수수료"
                  name="fee"
                  style={{ flex: 2 }}
                >
                  <Input
                    type="number"
                    placeholder="수수료"
                  />
                </Form.Item>

                <Form.Item
                  label="단위"
                  name="feetype"
                  style={{ flex: 1 }}
                >
                  <Select
                    options={[
                      { value: 0, label: '%' },
                      { value: 1, label: selectedPair?.feecurrency || 'USD' },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  label="슬리피지"
                  name="slip"
                  style={{ flex: 1 }}
                >
                  <Input
                    type="number"
                    placeholder="슬리피지"
                  />
                </Form.Item>
              </Space.Compact>
            </>
          )}
        </Form>
      </ModalContent>
    </Modal>
  )
}

export default AddPersonalContractModal